import { useQuery, useQueryClient } from '@tanstack/react-query'
import { compact } from 'lodash'
import moment from 'moment/moment'
import { useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Booking } from '../../../models/Booking'
import { ComputeFactory } from '../../../models/ComputeFactory'
import { Estimate } from '../../../models/Estimate'
import { Note } from '../../../models/Note'
import { RecordFactory } from '../../../models/RecordFactory'
import { Subaccount } from '../../../models/Subaccount'
import { User } from '../../../models/User'
import { Icon } from '../../Icon'
import { Spinner } from '../../Spinner'
import Button from '../../app/design/Button'
import Card from '../../app/design/Card'
import Label from '../../app/design/Label'
import Modal from '../../app/design/Modal'
import Paragraph from '../../app/design/Paragraph'
import { TextArea } from '../fields/TextArea'
import { TextField } from '../fields/TextField'
import { UploadField } from '../fields/UploadField'
import { WorkflowPage } from '../../workflows/WorkflowPage'

type Props = {
    target:
        | {
              type: 'user'
              user: User
          }
        | { type: 'subaccount'; subaccount: Subaccount }
        | { type: 'estimate'; estimate: Estimate }
        | { type: 'booking'; booking: Booking }
}

export const NotesSection = ({ target }: Props) => {
    const queryClient = useQueryClient()

    const queryKey =
        target.type === 'user'
            ? ['notes', 'user', target.user.id]
            : target.type === 'subaccount'
              ? ['notes', 'subaccount', target.subaccount.id]
              : target.type === 'estimate'
                ? ['notes', 'estimate', target.estimate.id]
                : ['notes', 'booking', target.booking.id]

    const { data: notes } = useQuery({
        queryKey,
        queryFn: async () => {
            if (target.type === 'user') {
                const notesResponse = await RecordFactory.getInstance().fetchNotes(target.user, '')
                if (!notesResponse.ok) {
                    throw new Error('Failed to fetch notes')
                }
                return notesResponse.value
            }
            else if (target.type === 'subaccount') {
                await RecordFactory.getInstance().fetchSubaccountNotes(target.subaccount.id ?? '')
                return await ComputeFactory.getInstance().fetchSubaccountNotes(target.subaccount)
            }
            else if (target.type === 'estimate') {
                if (!target.estimate.id) {
                    return []
                }

                await RecordFactory.getInstance().fetchEstimateNotes(target.estimate.id)
                return ComputeFactory.getInstance().fetchEstimateNotes(target.estimate.id)
            }
            else {
                if (!target.booking.id) {
                    return []
                }

                await RecordFactory.getInstance().fetchBookingNotes(target.booking.id)
                return ComputeFactory.getInstance().fetchBookingNotes(target.booking.id)
            }
        },
    })

    const [modalState, setModalState] = useState<
        | { type: 'list' }
        | { type: 'detail'; note: Note; onCompleteBehavior: 'back-to-list' | 'close' }
        | null
    >(null)

    const handleAddNote = () => {
        const newNote =
            target.type === 'user'
                ? new Note({ person: target.user })
                : target.type === 'subaccount'
                  ? new Note({ subaccount: target.subaccount })
                  : target.type === 'estimate'
                    ? new Note({ estimate: target.estimate })
                    : new Note({ booking: target.booking })

        setModalState({ type: 'detail', note: newNote, onCompleteBehavior: 'close' })
    }

    return (
        <>
            <Card
                className="flex flex-col items-stretch justify-start gap-y-8"
                title="Notes"
                count={notes !== undefined && notes.length > 0 ? notes.length : undefined}
                headerButtons={compact([
                    notes !== undefined &&
                        notes.length > 0 && {
                            title: 'See all',
                            onClick: () => setModalState({ type: 'list' }),
                        },
                    {
                        title: 'Add',
                        onClick: handleAddNote,
                    },
                ])}
            >
                {notes !== undefined ? (
                    notes.length > 0 ? (
                        <div className="flex flex-col items-stretch justify-center gap-y-8">
                            {notes.slice(0, 3).map((note) => (
                                <NoteListing
                                    key={note.id ?? ''}
                                    note={note}
                                    onSelect={() =>
                                        setModalState({
                                            type: 'detail',
                                            note,
                                            onCompleteBehavior: 'close',
                                        })
                                    }
                                />
                            ))}
                        </div>
                    ) : null
                ) : (
                    <div className="flex items-center justify-center">
                        <Spinner size={64} />
                    </div>
                )}
            </Card>

            <NotesListModal
                isOpen={modalState?.type === 'list'}
                onClose={() => setModalState(null)}
                notes={notes ?? []}
                onSelectNote={(note) =>
                    setModalState({ type: 'detail', note, onCompleteBehavior: 'back-to-list' })
                }
                onAddNote={handleAddNote}
            />

            {modalState?.type === 'detail' && (
                <NoteDetailModal
                    isOpen
                    onClose={() => setModalState(null)}
                    onBack={
                        modalState.onCompleteBehavior === 'back-to-list'
                            ? () => setModalState({ type: 'list' })
                            : null
                    }
                    note={modalState.note}
                    onFinish={() => {
                        queryClient.invalidateQueries({ queryKey })
                        if (modalState.onCompleteBehavior === 'back-to-list') {
                            setModalState({ type: 'list' })
                        }
                        else {
                            setModalState(null)
                        }
                    }}
                />
            )}
        </>
    )
}

const NoteListing = ({ note, onSelect }: { note: Note; onSelect?: () => void }) => {
    return (
        <button
            className="group relative flex w-full items-center justify-center gap-x-4 text-left"
            onClick={onSelect}
        >
            {onSelect && (
                <div className="absolute -inset-4 z-10 rounded-lg group-hover:bg-black/4" />
            )}

            <Icon icon="notepad.large" background="round" className="z-20 shrink-0" />

            <div className="z-20 flex grow flex-col items-stretch justify-center gap-y-1">
                <Label size="md" className="font-bold">
                    {note.title}
                </Label>
                <Paragraph size="sm" className="line-clamp-2 text-gray-600">
                    {note.note}
                </Paragraph>
                <Paragraph size="sm" className="text-gray-600">
                    {moment(note.createdDate).format('MMM D, YYYY')}
                </Paragraph>
            </div>

            {!!note.image?.url && (
                <img
                    src={note.image?.url}
                    className="z-20 size-16 shrink-0 rounded-sm object-cover"
                />
            )}
        </button>
    )
}

const NotesListModal = ({
    isOpen,
    onClose,
    notes,
    onSelectNote,
    onAddNote,
}: {
    isOpen: boolean
    onClose: () => void
    notes: Note[]
    onSelectNote: (note: Note) => void
    onAddNote: () => void
}) => {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div>
                    Notes{' '}
                    {notes.length > 0 && (
                        <Label size="md" className="text-gray-600">
                            ({notes.length})
                        </Label>
                    )}
                </div>
            }
            footer={
                <div>
                    <Button intent="secondary" onClick={onAddNote}>
                        Add a note
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col items-stretch justify-center gap-y-3">
                {notes.map((note) => (
                    <div
                        key={note.id ?? ''}
                        className="cursor-pointer rounded-lg border border-gray-300 p-4 hover:bg-black/4"
                        onClick={() => onSelectNote(note)}
                    >
                        <NoteListing note={note} />
                    </div>
                ))}
            </div>
        </Modal>
    )
}

const NoteDetailModal = ({
    isOpen,
    onClose,
    onBack,
    note,
    onFinish,
}: {
    isOpen: boolean
    onClose: () => void
    onBack: (() => void) | null
    note: Note
    onFinish: () => void
}) => {
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [title, setTitle] = useState(note.title ?? '')
    const [description, setDescription] = useState(note.note ?? '')
    const [photo, setPhoto] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(() => {
        const imageURL = note.image?.url
        if (imageURL === undefined) {
            return null
        }
        return { type: 'url', url: imageURL }
    })

    const handleSave = async () => {
        setIsSaving(true)
        const newNote = note.clone({ title, note: description })
        await RecordFactory.getInstance().sendNote(
            newNote,
            photo?.type === 'file' ? photo.file : undefined
        )
        setIsSaving(false)
        onFinish()
    }

    const handleDelete = async () => {
        if (note.id === undefined) {
            return
        }
        setIsDeleting(true)
        await RecordFactory.getInstance().deleteNote(note.id)
        setIsDeleting(false)
        onFinish()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            onNavigateBack={onBack ?? undefined}
            title="Note"
            footer={
                <div
                    className={twm(
                        'flex w-full items-center',
                        note.id !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {note.id !== undefined && (
                        <Button intent="destructive" isLoading={isDeleting} onClick={handleDelete}>
                            Delete
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={handleSave}
                        disabled={!title && !description && !photo}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col items-stretch justify-center gap-y-8">
                <WorkflowPage.IconSection icon="notepad.large" title="Title">
                    <TextField
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        onClear={() => setTitle('')}
                        size="medium"
                        placeholder="Enter title"
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    icon="note.large"
                    title="Description"
                    secondaryText={`${description.length} / 10,000`}
                >
                    <TextArea
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        onClear={() => setDescription('')}
                        size="medium"
                        placeholder="Enter description"
                        maxLength={10000}
                        isAutoResizing
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection icon="image.large" title="Image">
                    {photo === null ? (
                        <UploadField
                            buttonTitle="Add a photo"
                            className="h-[150px] p-6"
                            onSelectFiles={(files) => {
                                if (files.length === 0) {
                                    return
                                }
                                setPhoto({ type: 'file', file: files[0] })
                            }}
                            accept={['image/png', 'image/jpg', 'image/jpeg']}
                        />
                    ) : (
                        <div className="relative">
                            {photo.type === 'file' ? (
                                <img
                                    src={URL.createObjectURL(photo.file)}
                                    className="h-full max-h-[360px] w-full rounded-lg object-cover"
                                />
                            ) : (
                                <img
                                    src={photo.url}
                                    className="h-full max-h-[360px] w-full rounded-lg object-cover"
                                />
                            )}

                            <Button
                                shape="circle"
                                intent="secondary"
                                className="absolute top-4 right-4"
                                onClick={() => setPhoto(null)}
                            >
                                <Icon icon="trash.large" />
                            </Button>
                        </div>
                    )}
                </WorkflowPage.IconSection>
            </div>
        </Modal>
    )
}
